// src/roles/store/ReceiveMaterial.js
// v7 — Root-cause hardening: fresh Item Master fetch + block-on-mismatch
//
// CHANGES vs v6:
//   - fetchItemsMasterList() — returns Item Master directly (not just state)
//   - handleSubmit & handleShortClose refetch Item Master FRESH right before
//     matching, instead of relying on the mount-time snapshot
//   - Pre-check pass BEFORE any Firestore writes: if any item can't be
//     matched to Item Master, the whole GRN is blocked with a visible alert
//     listing exactly which item(s) — never a silent console.warn skip
//   - Any unexpected match-failure mid-write now throws (loud failure)
//     instead of silently skipping that item's stock credit

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  doc, getDoc, getDocs,
  collection, serverTimestamp
} from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { receiveGRN }                from "../../services/inventoryService";
import { completePurchaseReceipt }   from "../../services/purchaseReceiptService";

const userData = () => JSON.parse(localStorage.getItem("user") || "{}");

// Roles allowed to confirm Short Close
const CAN_SHORT_CLOSE = ["manager", "owner", "sm", "store"];

const getItemGRNStatus = (received, ordered) => {
  if (ordered === 0)          return "over";       // extra item not in PO
  if (received < ordered)     return "short";
  if (received === ordered)   return "exact";
  if (received > ordered)     return "over";
  return "short";
};

const getPOStatusAfterGRN = ({ hasShort, hasOver }) => {
  if (hasShort) return "short_closed";
  if (hasOver)  return "over_received";
  return "completed";
};

export default function ReceiveMaterial() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const me       = userData();

  const [po,          setPo]          = useState(null);
  const [items,       setItems]       = useState([]);
  const [itemsMaster, setItemsMaster] = useState([]);
  const [submitting,  setSubmitting]  = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  const [newItem,     setNewItem]     = useState({
    item_id: "", item_code: "", item_name: "",
    category: "", receive_now_qty: "", unit: "nos",
  });
  const [itemRates,   setItemRates]   = useState({}); // ✅ NEW — GRN pe rate enter hoga
  const [suggestions, setSuggestions] = useState([]);

  // Actual Freight (optional, whole-PO) — the PO's own freight_amount is
  // only ever an ESTIMATE captured before delivery. This is the real
  // number once the transporter/vendor invoice confirms it — stored
  // separately (purchase_receipts.actual_freight_amount /
  // purchase_orders.actual_freight_amount), never overwrites the
  // estimate, so both remain visible for later comparison on the PO
  // detail page.
  const [actualFreight, setActualFreight] = useState("");

  // Short Close modal state
  const [showShortClose,   setShowShortClose]   = useState(false);
  const [shortCloseReason, setShortCloseReason] = useState("");
  const [shortClosing,     setShortClosing]      = useState(false);
  // Items detected as short — shown in modal for review
  const [shortItems,       setShortItems]        = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadItemsMaster(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (id) fetchPO(); }, [id]);

  // Returns the fresh Item Master list directly (not just via state) — used
  // right before GRN submit so matching never runs against stale data that
  // was only ever fetched once at component mount. loadItemsMaster() below
  // wraps this for the mount-time load and keeps state in sync for the UI
  // (search suggestions, "Add Extra Item", etc).
  const fetchItemsMasterList = async () => {
    const { query, where } = await import("firebase/firestore");
    const snap = await getDocs(query(collection(db, "items"), where("company_id", "==", me.company_id || "")));
    const master = [];
    snap.forEach(d => {
      const item = { id: d.id, ...d.data() };
      if (!item.item_name?.trim()) return;
      master.push(item);
    });
    return master;
  };

  const loadItemsMaster = async () => {
    try {
      const master = await fetchItemsMasterList();
      setItemsMaster(master);
    } catch (err) {
      console.error("[ReceiveMaterial] loadItemsMaster error:", err);
    }
  };

  const fetchPO = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, "purchase_orders", id));
      if (!snap.exists()) { setError("Purchase order not found."); setLoading(false); return; }

      const poData = { id: snap.id, ...snap.data() };

      if (poData.company_id !== me.company_id) {
        setError("Access denied."); setLoading(false); return;
      }
      if (poData.grn_locked) {
        setError("GRN already completed for this PO."); setLoading(false); return;
      }
      if (poData.po_status !== "ordered") {
        setError(`GRN not allowed — PO is "${poData.po_status}". Must be Ordered first.`);
        setLoading(false); return;
      }

      setPo(poData);

      // Pre-fill received qty = full ordered qty (employee adjusts if less arrived)
      const mapped = (poData.items || [])
        .filter(item => !item._cancelled)
        .map(item => ({
          ...item,
          receive_now_qty: Number(item.ordered_qty || 0),
        }));
      setItems(mapped);
    } catch (err) {
      console.error("[ReceiveMaterial] fetchPO error:", err);
      setError("Failed to load purchase order.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = (val) => {
    if (!val?.trim()) { setSuggestions([]); return; }
    setSuggestions(
      itemsMaster
        .filter(i => i.item_name?.toLowerCase().includes(val.toLowerCase()))
        .slice(0, 8)
    );
  };

  const addExtraItem = () => {
    if (!newItem.item_name?.trim()) { alert("Select an item first."); return; }
    if (Number(newItem.receive_now_qty || 0) <= 0) { alert("Enter qty > 0."); return; }
    const existsIdx = items.findIndex(i =>
      newItem.item_id
        ? String(i.item_id).trim() === String(newItem.item_id).trim()
        : i.item_name?.toLowerCase() === newItem.item_name?.toLowerCase()
    );
    if (existsIdx >= 0) {
      const updated = [...items];
      updated[existsIdx].receive_now_qty =
        Number(updated[existsIdx].receive_now_qty || 0) + Number(newItem.receive_now_qty);
      setItems(updated);
    } else {
      setItems([...items, {
        ...newItem,
        received_qty: 0, ordered_qty: 0,
        receive_now_qty: Number(newItem.receive_now_qty),
        _extra: true,
      }]);
    }
    setNewItem({ item_id: "", item_code: "", item_name: "", category: "", receive_now_qty: "", unit: "nos" });
    setSuggestions([]);
  };

  // Shared matcher — same 4-try logic as before, now always called with a
  // freshly-fetched Item Master array passed in explicitly, never the
  // (possibly stale) component state.
  const findMasterMatch = (masterList, pItem) => {
    const pItemId   = String(pItem.item_id   || "").trim();
    const pItemCode = String(pItem.item_code  || "").trim();
    const pItemName = String(pItem.item_name  || "").toLowerCase().trim();
    return masterList.find(m =>
      (pItemId   && String(m.item_id   || "").trim() === pItemId)   ||
      (pItemCode && String(m.item_code || "").trim() === pItemCode) ||
      (pItemId   && String(m.id        || "").trim() === pItemId)   ||
      (pItemName && String(m.item_name || "").toLowerCase().trim() === pItemName)
    );
  };

  // ─────────────────────────────────────────────────────────
  // SUBMIT — validates quantities, blocks if any short
  // ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (submitting) return;

    // Rate validation — har item ka rate mandatory
    const missingRate = items.find(item => {
      const key = item.item_id || item.item_name;
      return !Number(itemRates[key] || 0);
    });
    if (missingRate) {
      alert(`Rate missing for: "${missingRate.item_name}"\nPlease enter rate (₹) for all items before submitting GRN.`);
      return;
    }

    // Detect short items from current filled quantities
    const detectedShortItems = items.filter(item => {
      const ordered    = Number(item.ordered_qty  || 0);
      const receiveNow = Number(item.receive_now_qty || 0);
      return ordered > 0 && receiveNow < ordered; // extra items (_extra) excluded
    });

    // ANY short → force Short Close modal — submit blocked
    if (detectedShortItems.length > 0) {
      setShortItems(detectedShortItems);
      setShortCloseReason("");
      setShowShortClose(true);
      return; // ← BLOCK. handleShortClose will proceed from modal.
    }

    // No shorts — only exact + over — proceed directly
    setSubmitting(true);
    try {
      const poRef  = doc(db, "purchase_orders", id);
      const poSnap = await getDoc(poRef);
      if (!poSnap.exists()) throw new Error("PO not found");
      const poData = poSnap.data();

      // Refetch Item Master FRESH right before matching — itemsMaster state
      // was only ever loaded once at component mount, so a long-open GRN
      // tab (or an item added/edited elsewhere in the meantime) could
      // otherwise silently match against stale data.
      const freshItemsMaster = await fetchItemsMasterList();
      setItemsMaster(freshItemsMaster);

      // Pre-check EVERY item that will need an inventory credit BEFORE
      // writing anything. If even one item can't be matched to Item
      // Master, abort the WHOLE GRN rather than silently skipping just
      // that item's stock credit — previously this only logged a
      // console.warn nobody saw, while the PO/receipt still showed
      // "exact" and the item's real stock stayed at 0.
      const unmatchedNames = [];
      for (const pItem of poData.items || []) {
        if (pItem._cancelled) continue;
        // Custom items (typed by hand, never in Item Master) are received
        // for record-keeping only — no inventory update, so they must
        // never be required to match Item Master (Constitution §5 /
        // CORE_RULES §6).
        if (pItem.is_custom_item) continue;
        const uiItem     = items.find(i =>
          String(i.item_id || "").trim() === String(pItem.item_id || "").trim()
        );
        const receiveNow = Number(uiItem?.receive_now_qty || 0);
        if (receiveNow <= 0) continue;
        if (!findMasterMatch(freshItemsMaster, pItem)) {
          unmatchedNames.push(pItem.item_name || pItem.item_id || "Unknown item");
        }
      }
      if (unmatchedNames.length > 0) {
        alert(
          `GRN blocked — the following item(s) could not be matched to Item Master, ` +
          `so their stock would NEVER be credited even though the PO would show "exact":\n\n` +
          unmatchedNames.map(n => `• ${n}`).join("\n") +
          `\n\nPlease check these items in Item Master (name/code match) and try again.`
        );
        setSubmitting(false);
        return;
      }

      const updatedItems = [];
      let hasOver = false;

      // ── Process PO items ──
      for (const pItem of poData.items || []) {
        if (pItem._cancelled) { updatedItems.push(pItem); continue; }

        const uiItem     = items.find(i =>
          String(i.item_id || "").trim() === String(pItem.item_id || "").trim()
        );
        const receiveNow  = Number(uiItem?.receive_now_qty || 0);
        const orderedQty  = Number(pItem.ordered_qty || 0);
        const overQty     = receiveNow > orderedQty ? receiveNow - orderedQty : 0;
        const status      = getItemGRNStatus(receiveNow, orderedQty);

        if (status === "over") hasOver = true;

        updatedItems.push({
          ...pItem,
          received_qty:     receiveNow,
          cancelled_qty:    0,           // no short in this path
          over_qty:         overQty,
          item_grn_status:  status,
        });

        // Inventory entry — received_qty only. Custom items are received
        // for record-keeping/costing only — no inventory_summary credit
        // (Constitution §5 / CORE_RULES §6) — so this whole block is
        // skipped for them.
        if (receiveNow > 0 && !pItem.is_custom_item) {
          const master = findMasterMatch(freshItemsMaster, pItem);

          console.log(`[GRN] Item: "${pItem.item_name}" | pItem.item_id="${pItem.item_id}" | master found: ${master?.item_id || "NOT FOUND"}`);

          if (!master?.item_id) {
            // Should be unreachable now (pre-checked above). If it somehow
            // still happens, fail loudly instead of silently skipping the
            // stock credit — a partial GRN with mismatched stock is worse
            // than a hard stop here.
            throw new Error(
              `Unexpected: no Item Master match for "${pItem.item_name}" during write. ` +
              `GRN aborted mid-way — please check inventory_summary manually for this PO before retrying.`
            );
          }

          let finalQty = receiveNow;
          if (master.conversion && uiItem?.unit === master.conversion.purchase_unit)
            finalQty = receiveNow * (master.conversion.factor || 1);
          await receiveGRN({
            company_id:     poData.company_id || "",
            item_id:        master.item_id,
            item_code:      master.item_code || "",
            item_name:      master.item_name,
            category:       master.category || "MATERIAL",
            qty:            finalQty,
            unit:           master.conversion?.base_unit || master.unit || "nos",
            rate:           Number(itemRates[pItem.item_id || pItem.item_name] || 0), // ✅ GRN pe rate
            reference_id:   id,
            reference_type: "purchase_grn",
            remarks:        overQty > 0 ? `GRN — Over receive (+${overQty})` : "GRN Material Receive",
          });
        }
      }

      // ── Process extra items (not in original PO) ──
      for (const uiItem of items.filter(i => i._extra)) {
        const receiveNow = Number(uiItem.receive_now_qty || 0);
        if (receiveNow <= 0) continue;
        hasOver = true;
        updatedItems.push({
          item_id:         uiItem.item_id  || "",
          item_code:       uiItem.item_code || "",
          item_name:       uiItem.item_name,
          category:        uiItem.category  || "",
          ordered_qty:     0,
          received_qty:    receiveNow,
          cancelled_qty:   0,
          over_qty:        receiveNow,  // all extra
          item_grn_status: "over",
          unit:            uiItem.unit || "nos",
          _extra:          true,
        });
        const master = freshItemsMaster.find(m =>
          String(m.item_id || "").trim() === String(uiItem.item_id || "").trim()
        );
        if (master?.item_id) {
          await receiveGRN({
            company_id:     poData.company_id || "",
            item_id:        master.item_id,
            item_code:      master.item_code || "",
            item_name:      master.item_name,
            category:       master.category || "MATERIAL",
            qty:            receiveNow,
            unit:           master.unit || "nos",
            rate:           Number(itemRates[uiItem.item_id || uiItem.item_name] || 0), // ✅
            reference_id:   id,
            reference_type: "purchase_extra_grn",
            remarks:        "Extra item — not in original PO",
          });
        }
      }

      // ── Grand Total — rate from UI itemRates ──
      const grandTotal = updatedItems.reduce((sum, item) =>
        item._cancelled ? sum : sum + (
          Number(item.received_qty || 0) *
          Number(itemRates[item.item_id || item.item_name] || 0)
        ), 0
      );

      // ── Rate embed in items — receipt SOT ke liye ──
      const itemsWithRate = updatedItems.map(item => ({
        ...item,
        rate: Number(itemRates[item.item_id || item.item_name] || 0),
      }));

      // ✅ Constitution: ALL business logic → service layer
      // UI MUST NEVER write Firestore directly
      await completePurchaseReceipt({
        purchase_order_id:  id,
        poData,
        updatedItems:       itemsWithRate,
        grandTotal,
        hasOver,
        receipt_type:       "full",
        short_close_reason: "",
        completed_by:       me.uid  || me.id || "",
        completed_by_name:  me.name || "",
        company_id:         poData.company_id || "",
        actualFreight:       Number(actualFreight || 0),
      });

      const msg = hasOver
        ? `⚠ Over-receive flagged — Manager review required`
        : "All items received correctly ✅";
      // ✅ Resolve po_ordered notification
      try {
        const { markNotificationReadByReference } = await import("../../services/notificationService");
        await markNotificationReadByReference(id, "po_ordered", poData.company_id || "");
      } catch (e) {
        console.warn("[GRN] resolve notification failed (non-blocking):", e);
      }

      alert(`GRN Submitted ✅\nPO: ${poData.po_number || id}\n${msg}`);
      navigate("/purchase-orders", { replace: true });

    } catch (err) {
      console.error("[GRN] error:", err);
      alert(err.message || "GRN failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // SHORT CLOSE — handles mixed short + over in same PO
  // Role-gated: Manager / SM / Owner only
  // ─────────────────────────────────────────────────────────
  const handleShortClose = async () => {
    if (!shortCloseReason.trim()) { alert("Reason is mandatory."); return; }

    // Rate validation
    const missingRate = items.find(item => {
      const key = item.item_id || item.item_name;
      return !Number(itemRates[key] || 0);
    });
    if (missingRate) {
      alert(`Rate missing for: "${missingRate.item_name}"\nPlease enter rate (₹) for all items.`);
      return;
    }

    const role = (me.role || "").toLowerCase().trim();
    if (!CAN_SHORT_CLOSE.includes(role)) {
      alert("Only Manager / SM / Owner can confirm Short Close.");
      return;
    }

    setShortClosing(true);
    try {
      const poRef  = doc(db, "purchase_orders", id);
      const poSnap = await getDoc(poRef);
      if (!poSnap.exists()) throw new Error("PO not found");
      const poData = poSnap.data();

      // Refetch Item Master FRESH right before matching — same reasoning
      // as handleSubmit above.
      const freshItemsMaster = await fetchItemsMasterList();
      setItemsMaster(freshItemsMaster);

      // Pre-check EVERY item that will need an inventory credit BEFORE
      // writing anything — abort the whole Short Close if any item can't
      // be matched, instead of silently skipping just that item.
      const unmatchedNames = [];
      for (const pItem of poData.items || []) {
        if (pItem._cancelled) continue;
        // Custom items — never required to match Item Master (no
        // inventory update for these, Constitution §5 / CORE_RULES §6).
        if (pItem.is_custom_item) continue;
        const uiItem     = items.find(i =>
          String(i.item_id || "").trim() === String(pItem.item_id || "").trim()
        );
        const receiveNow = Number(uiItem?.receive_now_qty || 0);
        if (receiveNow <= 0) continue;
        if (!findMasterMatch(freshItemsMaster, pItem)) {
          unmatchedNames.push(pItem.item_name || pItem.item_id || "Unknown item");
        }
      }
      if (unmatchedNames.length > 0) {
        alert(
          `Short Close blocked — the following item(s) could not be matched to Item Master, ` +
          `so their stock would NEVER be credited:\n\n` +
          unmatchedNames.map(n => `• ${n}`).join("\n") +
          `\n\nPlease check these items in Item Master (name/code match) and try again.`
        );
        setShortClosing(false);
        return;
      }

      const updatedItems = [];
      let hasOver = false;
      let totalOverQty = 0;
      let totalCancelledQty = 0;

      // ── Process PO items — every item gets its fate ──
      for (const pItem of poData.items || []) {
        if (pItem._cancelled) { updatedItems.push(pItem); continue; }

        const uiItem      = items.find(i =>
          String(i.item_id || "").trim() === String(pItem.item_id || "").trim()
        );
        const receiveNow  = Number(uiItem?.receive_now_qty || 0);
        const orderedQty  = Number(pItem.ordered_qty || 0);
        const cancelledQty = receiveNow < orderedQty
          ? orderedQty - receiveNow
          : 0;
        const overQty     = receiveNow > orderedQty
          ? receiveNow - orderedQty
          : 0;
        const status      = getItemGRNStatus(receiveNow, orderedQty);

        if (status === "over")  { hasOver = true; totalOverQty += overQty; }
        if (cancelledQty > 0)   totalCancelledQty += cancelledQty;

        updatedItems.push({
          ...pItem,
          received_qty:    receiveNow,
          cancelled_qty:   cancelledQty,   // ← explicitly dead qty
          over_qty:        overQty,
          item_grn_status: status,
        });

        // Inventory entry — received_qty only (cancelled qty never enters).
        // Custom items skipped here too — record-keeping only, no
        // inventory_summary credit (Constitution §5 / CORE_RULES §6).
        if (receiveNow > 0 && !pItem.is_custom_item) {
          const master = findMasterMatch(freshItemsMaster, pItem);

          if (!master?.item_id) {
            // Should be unreachable now (pre-checked above) — fail loudly
            // instead of silently skipping the stock credit.
            throw new Error(
              `Unexpected: no Item Master match for "${pItem.item_name}" during write. ` +
              `Short Close aborted mid-way — please check inventory_summary manually before retrying.`
            );
          }

          let finalQty = receiveNow;
          if (master.conversion && uiItem?.unit === master.conversion.purchase_unit)
            finalQty = receiveNow * (master.conversion.factor || 1);
          await receiveGRN({
            company_id:     poData.company_id || "",
            item_id:        master.item_id,
            item_code:      master.item_code || "",
            item_name:      master.item_name,
            category:       master.category || "MATERIAL",
            qty:            finalQty,
            unit:           master.conversion?.base_unit || master.unit || "nos",
            rate:           Number(itemRates[pItem.item_id || pItem.item_name] || 0), // ✅
            reference_id:   id,
            reference_type: "purchase_short_close_grn",
            remarks:        `Short Close: ${shortCloseReason}`,
          });
        }
      }

      // ── Extra items (not in PO) — treat as over ──
      for (const uiItem of items.filter(i => i._extra)) {
        const receiveNow = Number(uiItem.receive_now_qty || 0);
        if (receiveNow <= 0) continue;
        hasOver = true; totalOverQty += receiveNow;
        updatedItems.push({
          item_id: uiItem.item_id || "", item_code: uiItem.item_code || "",
          item_name: uiItem.item_name, category: uiItem.category || "",
          ordered_qty: 0, received_qty: receiveNow,
          cancelled_qty: 0, over_qty: receiveNow,
          item_grn_status: "over", unit: uiItem.unit || "nos", _extra: true,
        });
        const master = freshItemsMaster.find(m =>
          String(m.item_id || "").trim() === String(uiItem.item_id || "").trim()
        );
        if (master?.item_id) {
          await receiveGRN({
            company_id:     poData.company_id || "",
            item_id:        master.item_id,
            item_code:      master.item_code || "",
            item_name:      master.item_name,
            category:       master.category || "MATERIAL",
            qty:            receiveNow,
            unit:           master.unit || "nos",
            rate:           Number(itemRates[uiItem.item_id || uiItem.item_name] || 0), // ✅
            reference_id:   id,
            reference_type: "purchase_short_close_extra_grn",
            remarks:        `Short Close — Extra item: ${shortCloseReason}`,
          });
        }
      }

      // ── Grand Total — rate from UI itemRates ──
      const grandTotal = updatedItems.reduce((sum, item) =>
        item._cancelled ? sum : sum + (
          Number(item.received_qty || 0) *
          Number(itemRates[item.item_id || item.item_name] || 0)
        ), 0
      );

      // ── Rate embed in items — receipt SOT ke liye ──
      const itemsWithRate = updatedItems.map(item => ({
        ...item,
        rate: Number(itemRates[item.item_id || item.item_name] || 0),
      }));

      // ✅ Constitution: ALL business logic → service layer
      await completePurchaseReceipt({
        purchase_order_id:  id,
        poData,
        updatedItems:       itemsWithRate,
        grandTotal,
        hasOver,
        receipt_type:       "short_close",
        short_close_reason: shortCloseReason.trim(),
        completed_by:       me.uid  || me.id || "",
        completed_by_name:  me.name || "",
        company_id:         poData.company_id || "",
        actualFreight:       Number(actualFreight || 0),
      });

      // ✅ Resolve po_ordered notification
      try {
        const { markNotificationReadByReference } = await import("../../services/notificationService");
        await markNotificationReadByReference(id, "po_ordered", poData.company_id || "");
      } catch (e) {
        console.warn("[ShortClose] resolve notification failed (non-blocking):", e);
      }

      setShowShortClose(false);
      alert(`✅ PO Short Closed\nReceived items added to inventory.\nCancelled qty: ${totalCancelledQty} units permanently closed.`);
      navigate("/purchase-orders", { replace: true });

    } catch (err) {
      console.error("[ShortClose] error:", err);
      alert(err.message || "Short close failed.");
    } finally {
      setShortClosing(false);
    }
  };

  // ✅ Constitution: MRF notify moved to purchaseReceiptService.js (service layer)
  // notifyMRFsAfterGRN removed from UI component

  // ─────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────

  const canShortClose = CAN_SHORT_CLOSE.includes((me.role || "").toLowerCase().trim());

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-500 text-sm">Loading GRN...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 p-6 max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-800">← Back</button>
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-700 font-medium">{error}</p>
        <button onClick={() => navigate("/purchase-orders")}
          className="mt-4 px-4 py-2 bg-gray-200 rounded-lg text-sm hover:bg-gray-300">
          Go to Purchase Orders
        </button>
      </div>
    </div>
  );

  if (!po) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-800">← Back</button>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">

        {/* HEADER */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Receive Material (GRN)</h2>
            <p className="text-xs text-slate-400 mt-0.5">PO: {po.po_number || id} • {po.vendor_name || ""}</p>
          </div>
          {/* Manual Short Close — for when SM wants to close proactively */}
          {canShortClose && (
            <button
              onClick={() => {
                const detected = items.filter(i =>
                  Number(i.ordered_qty || 0) > 0 &&
                  Number(i.receive_now_qty || 0) < Number(i.ordered_qty || 0)
                );
                setShortItems(detected);
                setShortCloseReason("");
                setShowShortClose(true);
              }}
              className="px-4 py-2 rounded-xl bg-amber-100 text-amber-700 text-sm font-semibold hover:bg-amber-200">
              ⚠ Short Close PO
            </button>
          )}
        </div>

        {/* GRN RULES INFO BOX — correct message */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5 text-xs text-green-800">
          <strong>GRN Rules:</strong> Enter exact quantities received. If any item is less than
          ordered, Short Close is mandatory — remaining qty will be permanently cancelled.
          Over-receive is allowed and will be flagged for Manager review.
        </div>

        {/* ACTUAL FREIGHT — optional, whole-PO. The PO's own freight
            estimate (entered at creation time) rarely matches what's
            actually confirmed by the transporter/vendor invoice at
            delivery. This is purely a reference figure — never touches
            grand_total, the vendor-payable financial_transactions entry,
            or any inventory value. Leave blank if unknown; the PO's
            original estimate stays untouched either way. */}
        {po.freight_amount != null && (
          <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 mb-5">
            <p className="text-xs text-cyan-800 mb-2">
              <strong>Actual Freight (optional)</strong> — PO estimate was
              ₹{Number(po.freight_amount || 0).toLocaleString("en-IN")}. If the real transporter/
              vendor invoice confirms a different amount, enter it here for reference — this never
              changes the PO's original estimate.
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-cyan-700">₹</span>
              <input
                type="number" min="0" step="0.01"
                value={actualFreight}
                onChange={e => setActualFreight(e.target.value)}
                placeholder={String(po.freight_amount || 0)}
                className="w-40 border border-cyan-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
              />
            </div>
          </div>
        )}

        {/* ITEMS LIST */}
        <div className="space-y-3 mb-6">
          {items.map((item, i) => {
            const ordered    = Number(item.ordered_qty  || 0);
            const receiveNow = Number(item.receive_now_qty || 0);
            const isExtra    = item._extra || ordered === 0;
            const isOver     = ordered > 0 && receiveNow > ordered;
            const isExact    = ordered > 0 && receiveNow === ordered;
            const isShort    = ordered > 0 && receiveNow < ordered;

            return (
              <div key={i} className={`border rounded-2xl p-4 ${
                isOver  ? "border-amber-300 bg-amber-50"   :
                isExtra ? "border-purple-200 bg-purple-50" :
                isExact ? "border-green-300 bg-green-50"   :
                isShort ? "border-red-200 bg-red-50"       :
                          "border-slate-200 bg-white"
              }`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1 mr-3">
                    <p className="font-semibold text-slate-800 text-sm">{item.item_name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{item.unit || "nos"}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {ordered > 0 && (
                        <span className="text-xs text-slate-400">Ordered: {ordered}</span>
                      )}
                      {item.is_custom_item && (
                        <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">⚡ Custom</span>
                      )}
                      {isExtra && !item.is_custom_item && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">Extra</span>
                      )}
                      {isOver && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                          ⚠ Over +{receiveNow - ordered}
                        </span>
                      )}
                      {isExact && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Exact</span>
                      )}
                      {isShort && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                          ✕ Short — {ordered - receiveNow} will be cancelled
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {/* Qty */}
                    <div className="flex items-center gap-1">
                      <input
                        type="number" min="0"
                        value={item.receive_now_qty ?? 0}
                        onChange={e => {
                          const u = [...items];
                          u[i].receive_now_qty = Math.max(0, Number(e.target.value));
                          setItems(u);
                        }}
                        className={`w-20 border rounded-xl px-2 py-1.5 text-right text-sm outline-none focus:ring-2 ${
                          isShort ? "border-red-300 bg-red-50 focus:ring-red-300" :
                          isOver  ? "border-amber-300 focus:ring-amber-300" :
                                    "border-slate-200 focus:ring-blue-300"
                        }`}
                      />
                      <span className="text-xs text-slate-400 w-8">{item.unit || "nos"}</span>
                    </div>
                    {/* ✅ Rate — mandatory at GRN */}
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-slate-400">₹</span>
                      <input
                        type="number" min="0" placeholder="Rate *"
                        value={itemRates[item.item_id || item.item_name] || ""}
                        onChange={e => setItemRates(prev => ({
                          ...prev,
                          [item.item_id || item.item_name]: e.target.value,
                        }))}
                        className="w-24 border border-slate-200 rounded-xl px-2 py-1.5 text-right text-sm outline-none focus:ring-2 focus:ring-cyan-300"
                      />
                      {/* Amount preview */}
                      {Number(itemRates[item.item_id || item.item_name] || 0) > 0 && (
                        <span className="text-xs text-slate-500 whitespace-nowrap">
                          = ₹{(
                            Number(item.receive_now_qty || 0) *
                            Number(itemRates[item.item_id || item.item_name] || 0)
                          ).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ADD EXTRA ITEM */}
        <div className="mb-6 border border-dashed border-slate-300 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Add Extra Item (not in PO)</p>
          <div className="flex gap-2 items-center flex-wrap">
            <div className="flex-1 relative min-w-[160px]">
              <input
                type="text" autoComplete="off" placeholder="Search item..."
                value={newItem.item_name}
                onChange={e => {
                  setNewItem({ ...newItem, item_name: e.target.value, item_id: "" });
                  fetchSuggestions(e.target.value);
                }}
                className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300"
              />
              {suggestions.length > 0 && (
                <div className="absolute bg-white border rounded-xl shadow-lg w-full z-50 mt-1 max-h-40 overflow-y-auto">
                  {suggestions.map((s, idx) => (
                    <div key={idx}
                      onClick={() => {
                        setNewItem({
                          item_id: s.item_id || s.id || "", item_code: s.item_code || "",
                          item_name: s.item_name, category: s.category || s.item_category || "",
                          receive_now_qty: "", unit: s.unit || "nos",
                        });
                        setSuggestions([]);
                      }}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm">
                      {s.item_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <input type="number" min="0" placeholder="Qty" value={newItem.receive_now_qty}
              onChange={e => setNewItem({ ...newItem, receive_now_qty: e.target.value })}
              className="w-20 border border-slate-200 rounded-xl p-2.5 text-sm text-center outline-none" />
            <button onClick={addExtraItem}
              className="bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold">
              + Add
            </button>
          </div>
        </div>

        {/* SUBMIT BUTTON */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-3 rounded-xl bg-green-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-green-700 transition">
          {submitting ? "Processing GRN..." : "✓ Submit GRN & Update Inventory"}
        </button>

        {/* Short items preview — shown below button if any detected */}
        {items.some(i => Number(i.ordered_qty || 0) > 0 && Number(i.receive_now_qty || 0) < Number(i.ordered_qty || 0)) && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700">
            <strong>⚠ Short items detected — Short Close will be required on submit:</strong>
            <ul className="mt-1 space-y-0.5">
              {items
                .filter(i => Number(i.ordered_qty || 0) > 0 && Number(i.receive_now_qty || 0) < Number(i.ordered_qty || 0))
                .map((i, idx) => (
                  <li key={idx}>
                    • {i.item_name}: received {i.receive_now_qty} of {i.ordered_qty}
                    &nbsp;→ <span className="font-semibold">{Number(i.ordered_qty) - Number(i.receive_now_qty)} will be cancelled</span>
                  </li>
                ))
              }
            </ul>
          </div>
        )}
      </div>

      {/* ── SHORT CLOSE MODAL — force triggered on short detect ── */}
      {showShortClose && (
        <div className="fixed inset-0 bg-black/60 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 max-h-[90vh] overflow-y-auto">

            <h3 className="font-bold text-slate-800 text-base mb-1">⚠ Short Delivery Detected</h3>
            <p className="text-xs text-slate-500 mb-3">
              Neeche items ordered se kam aaye hain. Remaining qty permanently cancel hogi.
            </p>

            {/* Short items summary */}
            {shortItems.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 space-y-1">
                {shortItems.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="font-medium text-slate-700">{item.item_name}</span>
                    <span className="text-red-700 font-semibold">
                      {item.receive_now_qty} / {item.ordered_qty} received
                      &nbsp;— <span className="underline">{Number(item.ordered_qty) - Number(item.receive_now_qty)} cancelled</span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 text-xs text-amber-800">
              <strong>This will:</strong><br />
              • Add received quantities to inventory<br />
              • Permanently cancel remaining quantities<br />
              • Lock this PO — no future GRN possible<br />
              • Dobara chahiye to naya PO raise karna hoga
            </div>

            {/* Role check warning */}
            {!canShortClose && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-xs text-red-700 font-semibold">
                ⛔ Only Manager / SM / Owner can confirm Short Close.
                Please call your Manager to proceed.
              </div>
            )}

            <textarea
              rows={3}
              placeholder="Reason for short close * (mandatory)"
              value={shortCloseReason}
              onChange={e => setShortCloseReason(e.target.value)}
              autoFocus
              disabled={!canShortClose}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm resize-none outline-none mb-3 disabled:opacity-50"
            />

            <div className="flex gap-3">
              <button
                onClick={() => { setShowShortClose(false); setShortCloseReason(""); }}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm">
                ← Go Back
              </button>
              <button
                onClick={handleShortClose}
                disabled={shortClosing || !canShortClose || !shortCloseReason.trim()}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-semibold text-sm disabled:opacity-40">
                {shortClosing ? "Processing..." : "Confirm Short Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}